if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor () {
      super()
      this.isInitializing = false
      this.currentSlideIndex = 0
      this.slides = []
      this.slideChangeTimeout = null
      this.isHovering = false
      this.isIntersecting = true
    }

    connectedCallback () {
      this.slides = Array.from(this.querySelectorAll('.announcement-bar__block'))

      if (this.slides.length > 1) {
        this.initSlides()
        this.setupResizeObserver()
        this.setupIntersectionObserver()
        this.setupHoverPause()
        this.startAutoplay()
      }

      this.initCountdown()
      this.setupVisibilityHandling()
    }

    disconnectedCallback () {
      this.cleanup()
    }

    cleanup () {
      this.stopAutoplay()
      this.stopCountdown()

      if (this.slideChangeTimeout) {
        clearTimeout(this.slideChangeTimeout)
        this.slideChangeTimeout = null
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }

      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect()
        this.intersectionObserver = null
      }

      if (this.slidesContainer) {
        this.slidesContainer.removeEventListener('mouseenter', this.hoverEnterHandler)
        this.slidesContainer.removeEventListener('mouseleave', this.hoverLeaveHandler)
      }

      if (this.visibilityHandler) {
        document.removeEventListener('visibilitychange', this.visibilityHandler)
        this.visibilityHandler = null
        this.visibilityHandlerAdded = false
      }
    }

    setupVisibilityHandling () {
      if (this.visibilityHandlerAdded) return
      this.visibilityHandlerAdded = true

      this.visibilityHandler = () => {
        if (document.hidden) {
          this.stopAutoplay()
          this.stopCountdown()
        } else {
          this.calculateMaxHeight()
          if (this.slides.length > 1 && !this.isHovering && this.isIntersecting !== false) {
            this.startAutoplay()
          }
          this.startCountdown()
        }
      }

      document.addEventListener('visibilitychange', this.visibilityHandler)
    }

    initSlides () {
      if (this.isInitializing) return
      this.isInitializing = true

      try {
        this.calculateMaxHeight()
      } finally {
        this.isInitializing = false
      }
    }

    calculateMaxHeight () {
      if (this.slides.length === 0) return

      let maxHeight = 0

      this.slides.forEach(slide => {
        const height = slide.offsetHeight
        if (height > maxHeight) {
          maxHeight = height
        }
      })

      const container = this.querySelector('.announcement-bar__slides-container')
      if (container) {
        container.style.height = `${maxHeight}px`
      }
    }

    setupResizeObserver () {
      this.resizeObserver = new ResizeObserver(() => {
        this.calculateMaxHeight()
      })

      this.resizeObserver.observe(this)
    }

    setupIntersectionObserver () {
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            this.isIntersecting = entry.isIntersecting
            if (entry.isIntersecting) {
              // Element is in view - resume if not hovering
              if (!this.isHovering) {
                this.startAutoplay()
              }
            } else {
              // Element is out of view - pause
              this.stopAutoplay()
            }
          })
        },
        { threshold: 0 }, // Trigger when completely out of view
      )

      this.intersectionObserver.observe(this)
    }

    setupHoverPause () {
      this.slidesContainer = this.querySelector('.announcement-bar__slides-container')
      if (!this.slidesContainer) return

      this.isHovering = false

      this.hoverEnterHandler = () => {
        this.isHovering = true
        this.stopAutoplay()
      }

      this.hoverLeaveHandler = () => {
        this.isHovering = false
        this.startAutoplay()
      }

      this.slidesContainer.addEventListener('mouseenter', this.hoverEnterHandler)
      this.slidesContainer.addEventListener('mouseleave', this.hoverLeaveHandler)
    }

    startFadeTransition () {
      if (this.slides.length <= 1 || this.slideChangeTimeout) return

      const currentSlide = this.slides[this.currentSlideIndex]
      const nextSlideIndex = (this.currentSlideIndex + 1) % this.slides.length
      const nextSlide = this.slides[nextSlideIndex]

      // Fade out current slide
      currentSlide.classList.remove('announcement-bar__block--active')
      currentSlide.classList.add('announcement-bar__block--exiting')

      // After fade out (300ms) + gap (200ms), fade in next slide
      this.slideChangeTimeout = setTimeout(() => {
        currentSlide.classList.remove('announcement-bar__block--exiting')
        nextSlide.classList.add('announcement-bar__block--active')
        this.currentSlideIndex = nextSlideIndex
        this.slideChangeTimeout = null
      }, 500) // 300ms fade + 200ms gap
    }

    initCountdown () {
      if (this.countdownEndTime) {
        this.updateCountdown()
        this.startCountdown()
      }
    }

    startCountdown () {
      if (!this.countdownEndTime) return

      this.stopCountdown()
      this.countdownInterval = setInterval(() => {
        this.updateCountdown()
      }, 1000)
    }

    stopCountdown () {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
    }

    updateCountdown () {
      const countdownElements = this.querySelectorAll('.announcement-bar__countdown')
      if (!countdownElements.length || !this.countdownEndTime) return

      const now = new Date().getTime()
      const endTime = new Date(this.countdownEndTime).getTime()
      const timeLeft = endTime - now

      if (timeLeft <= 0) {
        countdownElements.forEach(element => {
          element.textContent = 'Expired'
        })
        this.stopCountdown()
        return
      }

      const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24))
      const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))

      let displayText = ''
      if (days > 0) {
        displayText = `${days} ${this.daysLabel}`
      } else {
        displayText = `${hours} ${this.hoursLabel}`
      }

      countdownElements.forEach(element => {
        element.textContent = displayText
      })
    }

    startAutoplay () {
      this.stopAutoplay()

      this.autoplayInterval = setInterval(() => {
        this.startFadeTransition()
      }, this.autoplaySpeed)
    }

    stopAutoplay () {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval)
        this.autoplayInterval = null
      }
    }

    get autoplaySpeed () {
      return parseInt(this.dataset.autoplaySpeed, 10) || 3000
    }

    get countdownEndTime () {
      return this.dataset.countdownEnd
    }

    get daysLabel () {
      return this.dataset.countdownDaysLabel || 'days'
    }

    get hoursLabel () {
      return this.dataset.countdownHoursLabel || 'hours'
    }
  }

  customElements.define('announcement-bar', AnnouncementBar)
}

if (!customElements.get('announcement-bar-text')) {
  class AnnouncementBarText extends HTMLElement {
    connectedCallback () {
      this.textEl = this.querySelector('.announcement-bar__text')
      if (!this.textEl) return

      this.measureHandler = () => this.measure()
      this.resizeObserver = new ResizeObserver(this.measureHandler)
      this.resizeObserver.observe(this)
      this.resizeObserver.observe(this.textEl)

      this.measure()
    }

    disconnectedCallback () {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
        this.resizeObserver = null
      }
    }

    measure () {
      const distance = Math.round(this.textEl.scrollWidth - this.clientWidth)

      if (distance > 1) {
        this.style.setProperty('--marquee-distance', `${distance}px`)
        this.dataset.overflowing = 'true'
      } else {
        this.style.removeProperty('--marquee-distance')
        delete this.dataset.overflowing
      }
    }
  }

  customElements.define('announcement-bar-text', AnnouncementBarText)
}
